import { useState } from 'react'
import './index.scss'

import { useAuth } from '@auth/AuthProvider'
import Modal, { ModalButtonStyle } from '@components/Modal'
import CategoryController from '@data/controller/CategoryController'
import { Category } from '@data/ChurchDataProvider'

import closeIcon from 'url:@icon/close.svg';

export default function CategoryModal(props: {
  isOpen: boolean,
  category?: Category | null,
  close: () => void,
  onSaved: (category: Category) => void
}) {
  const auth = useAuth();
  const modalTitle = props.category?.name ?? 'New Category';
  const [name, setName] = useState(props.category?.name ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const saveCategory = async () => {
    if (!auth.churchId) {
      setError('Missing church context. Please refresh and try again.');
      return;
    }

    const trimmedName = name.trim();

    if (trimmedName.length === 0) {
      setError('Please give this category a name.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const response = props.category
        ? await CategoryController.updateCategory(auth.churchId, props.category.id, trimmedName, auth)
        : await CategoryController.createCategory(auth.churchId, trimmedName, auth);

      if (!response.body) {
        setError(response.error || 'Unable to save this category right now.');
        setIsSaving(false);
        return;
      }

      props.onSaved(response.body);
    } finally {
      setIsSaving(false);
    }
  };

  return <Modal
    id='category-modal'
    isOpen={props.isOpen}
    footerButtons={[
      {
        title: props.category ? 'Save Changes' : 'Create Category',
        style: ModalButtonStyle.FILLED,
        enabled: !isSaving,
        onClick: saveCategory
      },
      {
        title: 'Cancel',
        style: ModalButtonStyle.OUTLINE,
        enabled: !isSaving,
        onClick: props.close
      }
    ]}>
    <div className='category-modal-content'>
      <div className='header'>
        <a className={isSaving ? 'close disabled' : 'close'}
          aria-disabled={isSaving}
          href='#'
          onClick={(e) => {
            e.preventDefault();

            if (isSaving) {
              return;
            }

            props.close();
          }}>
          <img className='icon' src={closeIcon} alt='Close icon' />
        </a>

        <h3 className='title'>{modalTitle}</h3>
      </div>

      {error ? <p className='save-error'>{error}</p> : null}

      <div className='body'>
        <div className='field-container'>
          <label htmlFor='category-name'>Name</label>
          <input
            id='category-name'
            type='text'
            name='category-name'
            placeholder='Category Name'
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
      </div>
    </div>
  </Modal>
}
